import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Home, MapPin, Phone } from "lucide-react";
import { BRAND } from "../data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrderConfirmedPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    axios
      .get(`${API}/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderId]);

  return (
    <div data-testid="order-confirmed-page" className="bg-cream min-h-screen pb-24">
      <header className="bg-[#FAF3E7] border-b border-[#E8D8C0]">
        <div className="max-w-3xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src={BRAND.logoTransparent}
              alt=""
              className="h-8 w-8 rounded-full ring-1 ring-[#C8A14B]/60 bg-[#3A1B0E]"
            />
            <span className="font-display text-cocoa">Andhra Biryani Co</span>
          </Link>
          <Link
            to="/"
            data-testid="confirmed-home-link"
            className="text-sm text-cocoa-soft hover:text-cocoa inline-flex items-center gap-1.5"
          >
            <Home size={14} /> Home
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-8 pt-12">
        {loading ? (
          <div className="text-center text-cocoa-soft">Loading…</div>
        ) : !order ? (
          <div className="text-center text-cocoa">
            <h1 className="font-display text-3xl">Order not found</h1>
            <Link
              to="/"
              className="mt-4 inline-block text-gold font-semibold underline"
            >
              Back to home
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center">
              <div className="inline-flex w-20 h-20 rounded-full bg-[#E8F7EA] items-center justify-center text-[#2E7D32]">
                <CheckCircle2 size={44} aria-hidden="true" />
              </div>
              <h1 className="mt-6 font-display text-4xl md:text-5xl text-cocoa">
                Order Confirmed
              </h1>
              <p className="mt-2 text-cocoa-soft">
                Thank you, <strong>{order.customer_name}</strong> — your handi
                is on the way.
              </p>
              <p
                data-testid="confirmed-order-id"
                className="mt-3 inline-block bg-card-warm border border-cream-deep rounded-full px-4 py-1.5 text-xs uppercase tracking-wider text-cocoa-soft"
              >
                Order #{order.id.slice(0, 8).toUpperCase()}
              </p>
            </div>

            <section className="mt-10 bg-white border border-cream-deep rounded-2xl p-6">
              <h2 className="font-display text-xl text-cocoa">Items</h2>
              <ul className="mt-4 divide-y divide-[#F0E2C8]">
                {order.items.map((it) => (
                  <li key={it.item_id} className="py-3 flex items-center gap-4">
                    <img
                      src={it.image}
                      alt={it.name}
                      className="w-14 h-14 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-cocoa truncate">
                        {it.name}
                      </div>
                      <div className="text-xs text-cocoa-soft">
                        ₹{it.unit_price} × {it.qty}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-cocoa">
                      ₹{it.amount}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-4 border-t border-[#E8D8C0] grid grid-cols-2 gap-y-1 text-sm">
                <span className="text-cocoa-soft">Item total</span>
                <span className="text-right text-cocoa">₹{order.subtotal}</span>
                <span className="text-cocoa-soft">Delivery fee</span>
                <span className="text-right text-cocoa">
                  {order.delivery_fee === 0 ? "FREE" : `₹${order.delivery_fee}`}
                </span>
                <span className="text-cocoa-soft">Taxes</span>
                <span className="text-right text-cocoa">₹{order.taxes}</span>
                <span className="font-display text-lg text-cocoa mt-2">Total Paid</span>
                <span
                  data-testid="confirmed-total"
                  className="text-right font-display text-lg text-cocoa mt-2"
                >
                  ₹{order.total}
                </span>
              </div>
            </section>

            <section className="mt-6 bg-white border border-cream-deep rounded-2xl p-6 grid sm:grid-cols-2 gap-4 text-sm">
              <div className="flex gap-3 items-start">
                <MapPin size={18} className="text-gold shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                    Delivering to
                  </div>
                  <div className="text-cocoa mt-1">{order.address}</div>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <Phone size={18} className="text-gold shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                    Contact
                  </div>
                  <div className="text-cocoa mt-1">{order.phone}</div>
                </div>
              </div>
            </section>

            <div className="mt-10 text-center">
              <Link
                to="/"
                data-testid="confirmed-back-home"
                className="inline-flex items-center gap-2 rounded-full bg-[#C8A14B] text-[#3A1B0E] hover:bg-[#D4A24E] px-7 py-3 font-semibold shadow-md transition-all"
              >
                Back to Home
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
